// Item stacks + slot containers (ARCH §3.4, BLK-6, PLR-1). PURE (ENG-1).
//
// An `ItemStack` is `{item, count, components?}`; the empty slot is item 0
// (ITEM_EMPTY) with count 0. Stacking respects the per-item max (64/16/1) and
// component equality (a damaged/enchanted tool never stacks). Containers expose
// the small `Slots` interface that every transaction op (transaction.ts) drives —
// no system mutates a slot array directly (PLR-1: one code path for solo clicks
// and server-arbitrated ops in v1.1).
import { ITEM_EMPTY } from '../registry/load';
import { quintileKey } from '../heredity/crops';

/** Item NBT-equivalent (ARCH §3.4). All optional; absence means a plain stack. */
export interface ItemComponents {
  /** Durability consumed so far (PLR-2 tools); 0/absent = pristine. */
  damage?: number;
  enchants?: ReadonlyArray<{ id: string; lvl: number }>;
  /** Accumulated anvil prior-work count → penalty 2ⁿ−1 (MB.4). */
  repairCost?: number;
  potion?: string;
  name?: string;
  lore?: readonly string[];
  trimId?: string;
  mapId?: number;
  /** A bucketed tropical fish's livery, so releasing it restores the same fish. */
  fishVariant?: number;
  /** Shulker/bundle contents (M6+). */
  storedContents?: ItemStack[];
  /** A genetic seed's genome (HER-8, ARCH §28.10), base64. Compared for stacking
   *  by `quintileKey`, not by bytes (D-TF-132) — see `componentsEqual` below. */
  genome?: string;
}

export interface ItemStack {
  /** u16 item id; ITEM_EMPTY (0) marks an empty slot. */
  item: number;
  /** 0..maxStack. Zero ⇒ empty regardless of `item`. */
  count: number;
  components?: ItemComponents;
}

/** A fresh empty stack (slots hold their own object so edits never alias). */
export function emptyStack(): ItemStack {
  return { item: ITEM_EMPTY, count: 0 };
}

export function isEmpty(s: ItemStack): boolean {
  return s.item === ITEM_EMPTY || s.count <= 0;
}

export function makeStack(item: number, count = 1, components?: ItemComponents): ItemStack {
  return components ? { item, count, components } : { item, count };
}

export function cloneStack(s: ItemStack): ItemStack {
  return s.components ? { item: s.item, count: s.count, components: cloneComponents(s.components) } : { item: s.item, count: s.count };
}

function cloneComponents(c: ItemComponents): ItemComponents {
  // Structured-ish clone: enough for the value types we carry (no functions).
  return JSON.parse(JSON.stringify(c)) as ItemComponents;
}

/** Deterministic component equality (stack merging predicate). `genome` compares
 *  by its quintile signature, not by bytes (D-TF-132, HER-8): two seeds with
 *  different exact genomes but the same five star ratings stack, and the merge
 *  keeps whichever stack was already in the slot — this predicate only says
 *  whether they MAY merge, never which genome the result carries. */
export function componentsEqual(a: ItemComponents | undefined, b: ItemComponents | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  if (quintileKey(a.genome) !== quintileKey(b.genome)) return false;
  const { genome: _ag, ...restA } = a;
  const { genome: _bg, ...restB } = b;
  return canonical(restA) === canonical(restB);
}

/** Stable JSON with sorted keys so `{a,b}` and `{b,a}` compare equal. */
function canonical(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  const obj = v as Record<string, unknown>;
  const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`).join(',')}}`;
}

/** Two non-empty stacks that may merge: same item and identical components. */
export function canStack(a: ItemStack, b: ItemStack): boolean {
  if (isEmpty(a) || isEmpty(b)) return false;
  return a.item === b.item && componentsEqual(a.components, b.components);
}

// ── Slots: the minimal container surface transactions operate on ──────────────
export interface Slots {
  readonly size: number;
  get(i: number): ItemStack;
  set(i: number, s: ItemStack): void;
  /** Per-item stack ceiling (64/16/1). */
  maxStackOf(item: number): number;
  /** Slot filter (armor/fuel/output slots); empty stacks always accepted. */
  accepts(i: number, s: ItemStack): boolean;
}

/** Whether slot `i` accepts `s` (empty is always removable). */
export function slotAccepts(slots: Slots, i: number, s: ItemStack): boolean {
  return isEmpty(s) || slots.accepts(i, s);
}

export type SlotFilter = (index: number, stack: ItemStack) => boolean;

/** A flat, filterable slot array — the base for player inventory / containers. */
export class Inventory implements Slots {
  readonly slots: ItemStack[];

  constructor(
    readonly size: number,
    private readonly maxStack: (item: number) => number,
    private readonly filter?: SlotFilter,
  ) {
    this.slots = Array.from({ length: size }, emptyStack);
  }

  get(i: number): ItemStack {
    return this.slots[i] ?? emptyStack();
  }

  set(i: number, s: ItemStack): void {
    this.slots[i] = isEmpty(s) ? emptyStack() : s;
  }

  maxStackOf(item: number): number {
    return item === ITEM_EMPTY ? 64 : Math.max(1, this.maxStack(item));
  }

  accepts(i: number, s: ItemStack): boolean {
    if (i < 0 || i >= this.size) return false;
    return this.filter ? this.filter(i, s) : true;
  }

  /** Total count of `item` across every slot. */
  countOf(item: number): number {
    let n = 0;
    for (const s of this.slots) if (s.item === item && s.count > 0) n += s.count;
    return n;
  }

  /** First slot index holding `item` (or -1). */
  find(item: number): number {
    return this.slots.findIndex((s) => s.item === item && s.count > 0);
  }

  firstEmpty(): number {
    return this.slots.findIndex(isEmpty);
  }

  clear(): void {
    for (let i = 0; i < this.size; i++) this.slots[i] = emptyStack();
  }

  /** Snapshot as plain stacks (persistence §3.6) — empty slots become null. */
  toJSON(): (ItemStack | null)[] {
    return this.slots.map((s) => (isEmpty(s) ? null : cloneStack(s)));
  }

  loadJSON(data: readonly (ItemStack | null)[] | undefined): void {
    this.clear();
    if (!data) return;
    for (let i = 0; i < this.size && i < data.length; i++) {
      const s = data[i];
      if (s && !isEmpty(s)) this.slots[i] = cloneStack(s);
    }
  }
}

// ── Player inventory layout (survival) ────────────────────────────────────────
// 0..8 hotbar · 9..35 storage · 36..39 armor (helmet/chest/legs/boots) · 40 offhand.
export const INV = {
  HOTBAR_START: 0,
  HOTBAR_END: 9, // exclusive
  STORAGE_START: 9,
  STORAGE_END: 36,
  ARMOR_START: 36,
  ARMOR_END: 40,
  OFFHAND: 40,
  SIZE: 41,
} as const;

/** Armor piece a slot expects (helmet=0 … boots=3); armor items resolve to one. */
export type ArmorSlot = 0 | 1 | 2 | 3;

export class PlayerInventory extends Inventory {
  /** Selected hotbar column (0..8). */
  selected = 0;

  constructor(
    maxStack: (item: number) => number,
    /** Which armor slot (0..3) an item fits, or null if it isn't armor. */
    private readonly armorSlotOf: (item: number) => ArmorSlot | null = () => null,
  ) {
    super(INV.SIZE, maxStack, (i, s) => {
      if (i >= INV.ARMOR_START && i < INV.ARMOR_END) {
        const want = (i - INV.ARMOR_START) as ArmorSlot;
        return armorSlotOf(s.item) === want;
      }
      return true;
    });
  }

  /** Slot indices scanned first when auto-collecting (hotbar then storage). */
  static readonly PICKUP_RANGE: readonly number[] = range(INV.HOTBAR_START, INV.STORAGE_END);
  static readonly HOTBAR: readonly number[] = range(INV.HOTBAR_START, INV.HOTBAR_END);
  static readonly STORAGE: readonly number[] = range(INV.STORAGE_START, INV.STORAGE_END);

  heldSlot(): number {
    return INV.HOTBAR_START + this.selected;
  }

  held(): ItemStack {
    return this.get(this.heldSlot());
  }

  isArmor(item: number): boolean {
    return this.armorSlotOf(item) !== null;
  }
}

export function range(startInclusive: number, endExclusive: number): number[] {
  const out: number[] = [];
  for (let i = startInclusive; i < endExclusive; i++) out.push(i);
  return out;
}
